import React from "react";
import { LegalLayout, Section } from "../components/LegalLayout";

export default function Refund() {
  return (
    <LegalLayout
      title="Refund Policy"
      subtitle="When and how you can request a refund on your Premier season pass."
      lastUpdated="14 February 2026"
    >
      <Section id="summary" title="Summary">
        <p>
          We offer a <strong>14-day cooling-off period</strong> on the £9.99 Premier season pass — full refund,
          no questions asked, provided you haven&apos;t used Premier features. After 14 days, refunds are at our
          discretion based on the cases below.
        </p>
      </Section>

      <Section id="cooling-off" title="1. 14-Day Cooling-Off Period">
        <p>
          Under the UK Consumer Contracts Regulations 2013, you have <strong>14 days</strong> from the date of
          purchase to cancel your Premier subscription for any reason and receive a full refund.
        </p>
        <p>
          <strong>Exception:</strong> if you have actively used Premier features (created a private league,
          accessed full-season fixtures, viewed the stats dashboard, or your email-only fixture lock has
          passed for one or more matchweeks during the period), you may waive the cooling-off right.
          We will assess each request on a case-by-case basis.
        </p>
      </Section>

      <Section id="after-14-days" title="2. Refunds After 14 Days">
        <p>After the 14-day window, refunds are not guaranteed but may be considered in these cases:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Service outage</strong> — if PitchIQ is unavailable for more than 72 consecutive hours during a matchweek.</li>
          <li><strong>Billing error</strong> — duplicate charges, accidental purchases, or incorrect amounts.</li>
          <li><strong>Feature regression</strong> — if a Premier feature you specifically purchased for is removed.</li>
        </ul>
        <p>
          Refund requests after the season has ended (i.e. after matchweek 38) will <strong>not</strong> be granted,
          as the subscription benefit has been delivered in full.
        </p>
      </Section>

      <Section id="not-eligible" title="3. What Is NOT Refundable">
        <p>The following are <strong>not eligible</strong> for refunds:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Dissatisfaction with your leaderboard position or prediction accuracy.</li>
          <li>Failure to make predictions before kickoff lock — picks are your responsibility.</li>
          <li>Disputes about API-Sports fixture results (we apply the data we receive — see Terms §8).</li>
          <li>Accounts terminated for Terms violations.</li>
          <li>Partial refunds for unused months (the Premier pass is a single 365-day grant, not a recurring sub).</li>
        </ul>
      </Section>

      <Section id="how-to-request" title="4. How to Request a Refund">
        <p>Email <a href="mailto:info@pitchiq.win" className="text-primary-yellow underline">info@pitchiq.win</a> with:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Your account email</li>
          <li>Approximate date of purchase</li>
          <li>Stripe receipt number (in your purchase confirmation email)</li>
          <li>Brief reason for the request</li>
        </ul>
        <p>We aim to acknowledge within 2 working days and process eligible refunds within 14 days.</p>
      </Section>

      <Section id="processing" title="5. How Refunds Are Processed">
        <p>
          Approved refunds are issued via Stripe back to the original payment method used. Funds typically
          appear within <strong>5–10 working days</strong>, depending on your bank or card issuer.
        </p>
        <p>
          When a refund is processed, your account immediately reverts to the free tier. Any private leagues
          you created remain in place but will be inaccessible to you unless you re-subscribe.
        </p>
      </Section>

      <Section id="disputes" title="6. Chargebacks &amp; Disputes">
        <p>
          Please contact us <strong>first</strong> at <a href="mailto:info@pitchiq.win" className="text-primary-yellow underline">info@pitchiq.win</a> before
          initiating a card chargeback. We can almost always resolve issues directly and faster than the dispute process.
        </p>
        <p>
          Fraudulent chargebacks (where Premier features have been used) will result in account suspension and may be
          referred to Stripe&apos;s Radar fraud detection.
        </p>
      </Section>

      <Section id="rights" title="7. Your Statutory Rights">
        <p>
          This policy does not affect your statutory rights under UK consumer law, including the Consumer Rights Act 2015
          and the Consumer Contracts Regulations 2013.
        </p>
      </Section>
    </LegalLayout>
  );
}
